import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog";
import { Button } from "../../../Components/UI/button";
import { X, Check, Mail, Settings, Link, PlayCircle, ArrowLeft } from "lucide-react";
import { AlertBox } from "../../../Components/Common/AlertBox";
import { useEffect, useState } from "react";
import { Input } from "../../../Components/UI/input";
import { addEmailConfigurationData, getEmailConfigurationData, getGoogleReviewLink, setGoogleReviewLink, getGmailAuthUrl, saveGmailOAuthTokens, unlinkGmailAccount } from "./PoliciesAPI";
import { useSelector } from "react-redux";
import { getAuthData } from "../../../Utils/AuthUtils";
import { toast } from "sonner";
import FieldWithIcon from "../../../Components/Common/FieldWithIcon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../Components/UI/tooltip";
import useIsMobile from "../../../Hooks/useIsMobile";

const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isIOSWebViewGoldApp = () => {
    const ua = navigator.userAgent.toLowerCase();
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // if (!isIOSDevice) return false;
    
    // const isNativeApp = ua.includes('webviewgold') || 
    //                     ua.includes('wkwebview') ||
    //                     window.webkit !== undefined;
    
    return isIOSDevice;
};

// Configuration types with their respective settings
const CONFIG_TYPES = {
    EMAIL: {
        title: "Email Configuration",
        icon: Mail,
        description: "Configure your email account to send emails directly from your own email address.",
        // fields: [
        //     {
        //         key: "email",
        //         label: "Email Address",
        //         type: "email",
        //         placeholder: "e.g. company-name@gmail.com",
        //         validation: (value) => {
        //             if (!value) return "Please enter a email address.";
        //             if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email address.";
        //             return null;
        //         }
        //     },
        //     {
        //         key: "password",
        //         label: "App Password",
        //         type: "password",
        //         placeholder: "Enter your email password",
        //         component: "FieldWithIcon",
        //         iconTypes: ["key"],
        //         validation: (value) => !value ? "Please enter a password." : null
        //     }
        // ],
        apiMethods: {
            save: async (data) => {
                const result = await addEmailConfigurationData({ Email: data.email, Password: data.password }, 'EMAIL');
                if (result.error === false && result.message == "Success") {
                    toast.success("Email configuration saved.")
                }
            },
            fetch: getEmailConfigurationData,
            mapFetchedData: (result) => ({
                email: result?.OwnEmail || "",
                password: result?.OwnEmailPwd || ""
            })
        }
    },
    GOOGLE_REVIEW_LINK: {
        title: "Google Review Link Configuration",
        icon: Link,
        description: "Set your Google Review link to collect customer feedback and reviews.",
        fields: [
            {
                key: "reviewLink",
                label: "Google Review Link",
                type: "url",
                placeholder: "https://g.page/r/your-business-review-link",
                validation: (value) => {
                    if (!value) return "Google Review Link is required.";
                    try {
                        new URL(value);
                        if (!value.includes('google') && !value.includes('g.page')) {
                            return "Please enter a valid Google Review link.";
                        }
                        return null;
                    } catch {
                        return "Please enter a valid URL.";
                    }
                }
            }
        ],
        apiMethods: {
            save: async (data) => {
                const result = await setGoogleReviewLink(data.reviewLink, 'GRLINK');
                if (result.error === false && result.message == "Success") {
                    toast.success("Google review link configuration saved.")
                }
            },
            fetch: async () => {
                const reviewLink = getGoogleReviewLink();
                return { reviewLink };
            },
            mapFetchedData: (result) => ({
                reviewLink: result?.reviewLink || ""
            })
        }
    }
};

export default function Policies({ open, onOpenChange, configType = "EMAIL" }) {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [isLinkingGmail, setIsLinkingGmail] = useState(false);
    const [dialogStates, setDialogStates] = useState({
        confirmUnlink: false
    });
    const isMobile = useIsMobile();

    const isOAuthEnabled = useSelector((state) => state.masterData?.isOAuthEnabled || false);

    const config = CONFIG_TYPES[configType];
    const IconComponent = config?.icon || Settings;

    useEffect(() => {
        if (config) {
            const initialData = {};
            (config?.fields || []).forEach(field => {
                initialData[field.key] = "";
            });
            setFormData(initialData);
        }
    }, [configType]);

    // Handle Gmail OAuth unlinking
    const handleUnlinkGmail = async () => {
        try {
            const result = await unlinkGmailAccount();

            if (result.error === false && result.message === "Success") {
                toast.success("Gmail account unlinked.");
                onOpenChange(false);
            } else {
                toast.error(result.message || "Failed to unlink Gmail account");
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to unlink Gmail account");
        }
    };

    // Handle Gmail OAuth linking
    const handleLinkGmail = async () => {
        try {
            if (isIOSWebViewGoldApp()) {
                toast.error("Please use desktop to connect!");
                return;
            }

            setIsLinkingGmail(true);
            const authData = getAuthData();
            const agencyId = authData?.AgencyID;

            if (!agencyId) {
                toast.error("Agency ID not found");
                return;
            }

            // Get authorization URL from C# backend
            const response = await getGmailAuthUrl();

            if (response.error === false && response.updatePackage) {
                // Parse the DataSet structure
                const parsedData = JSON.parse(response.updatePackage);
                const authUrl = parsedData?.Table?.[0]?.AuthUrl;
                
                if (authUrl) {
                    if (isMobile) {
                        window.location.href = authUrl;
                    } else {
                        const width = 500;
                        const height = 600;
                        const left = window.screen.width / 2 - width / 2;
                        const top = window.screen.height / 2 - height / 2;

                        const popup = window.open(
                            authUrl,
                            'Google OAuth',
                            `width=${width},height=${height},left=${left},top=${top}`
                        );

                        const handleMessage = async (event) => {
                            if (event.origin !== window.location.origin) return;

                            const { code } = event.data;

                            if (code) {
                                try {
                                    const result = await saveGmailOAuthTokens(code);

                                    if (result.error === false && result.message === "Success") {
                                        toast.success("Gmail account linked.");
                                        onOpenChange(false);
                                    } else {
                                        toast.error(result.message || "Failed to link Gmail account");
                                    }
                                } catch (error) {
                                    toast.error("Error linking Gmail account");
                                    console.log(error);
                                } finally {
                                    window.removeEventListener('message', handleMessage);
                                    if (popup) popup.close();
                                }
                            }
                        };

                        window.addEventListener('message', handleMessage);
                    }
                } else {
                    toast.error("Failed to get authorization URL");
                }
            } else {
                toast.error("Failed to get authorization response");
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to initiate Gmail linking");
        } finally {
            setIsLinkingGmail(false);
        }
    };

    // Validation function - now shows toast for first error
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;
        let firstError = null;

        (config?.fields || []).forEach(field => {
            if (field.validation) {
                const error = field.validation(formData[field.key]);
                if (error) {
                    newErrors[field.key] = error;
                    if (isValid) { // Capture only the first error
                        firstError = error;
                    }
                    isValid = false;
                }
            }
        });

        setErrors(newErrors);

        // Show toast for first validation error
        if (!isValid && firstError) {
            toast.error(firstError);
        }

        return isValid;
    };

    const handleSaveConfiguration = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            if (config.apiMethods.save) {
                await config.apiMethods.save(formData);
                onOpenChange(false);
            } else {
                toast.error("Save method not implemented for this configuration type.");
            }
        } catch (error) {
            toast.error(error.message || `Failed to save ${config.title.toLowerCase()}.`);
        }
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    const handleInputChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
        // Clear error for this field if it exists
        if (errors[key]) {
            setErrors(prev => ({
                ...prev,
                [key]: null
            }));
        }
    };

    // Fetch configuration data when dialog opens
    useEffect(() => {
        if (open && config) {
            const fetchConfiguration = async () => {
                try {
                    if (config.apiMethods.fetch) {
                        const result = await config.apiMethods.fetch();
                        const mappedData = config.apiMethods.mapFetchedData
                            ? config.apiMethods.mapFetchedData(result)
                            : result;
                        setFormData(mappedData);
                    }
                } catch (error) {
                    toast.error(error.message || `Failed to fetch ${config.title.toLowerCase()}.`);
                }
            };
            fetchConfiguration();
        } else {
            // Reset form when dialog is closed
            if (config) {
                const resetData = {};
                (config?.fields || []).forEach(field => {
                    resetData[field.key] = "";
                });
                setFormData(resetData);
                setErrors({});
            }
        }
    }, [open, config]);

    if (!config) {
        return (
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="lg:max-w-[20vw] w-full gap-0">
                    <DialogHeader>
                        <DialogTitle>Configuration Error</DialogTitle>
                    </DialogHeader>
                    <div className="p-4">
                        <p>Invalid configuration type: {configType}</p>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    const renderField = (field) => {
        const commonProps = {
            value: formData[field.key] || "",
            onChange: (e) => handleInputChange(field.key, e.target.value),
            className: `w-full border py-2 px-3 rounded-md h-11 ${errors[field.key] ? 'border-red-500' : ''}`,
            placeholder: field.placeholder
        };

        if (field.component === "FieldWithIcon") {
            return (
                <div key={field.key}>
                    <FieldWithIcon
                        fieldID={`${configType.toLowerCase()}-${field.key}`}
                        label={field.label}
                        placeholder={field.placeholder}
                        value={formData[field.key] || ""}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        iconTypes={field.iconTypes}
                        className="space-y-1.5"
                    />
                </div>
            );
        }

        return (
            <div key={field.key}>
                <label className="block text-sm font-medium mb-1 text-[var(--text-color)]">
                    {field.label}
                </label>
                <Input
                    type={field.type}
                    {...commonProps}
                />
            </div>
        );
    };

    const handleTutorialClick = () => {
        window.open("https://www.youtube.com/watch?v=aUizWiS03XQ", "_blank");
    };

    return (
        <>
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent
                    className={`!z-[10030] ${configType === "GOOGLE_REVIEW_LINK" ? "lg:max-w-[30vw]" : "lg:max-w-[20vw]"} w-full gap-0 overflow-visible ${configType === "EMAIL" ? "lg:max-w-[23vw]" : ""} p-0`}
                    overlayClassName="!z-[10029]"
                >
                    <DialogHeader >
                        {/* Mobile View */}
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-6 w-6" />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="text-lg font-medium flex items-center gap-2 min-w-0">
                                    <div className="truncate">
                                        {config.title}
                                    </div>
                                </DialogTitle>
                            </div>
                        </div>

                        {/* Desktop Title */}
                        <DialogTitle className="hidden sm:flex items-center gap-2 text-lg md:text-xl">
                            <IconComponent className="h-5 w-5" />
                            {config.title}
                        </DialogTitle>

                        {/* Desktop Right Side */}
                        <div className="hidden sm:flex items-center gap-2">
                            <DialogClose className="flex-shrink-0 p-1 sm:text-text-color sm:hover:bg-gray-200 rounded-md transition-colors">
                                <X className="h-5 w-5" />
                                <span className="sr-only">Close</span>
                            </DialogClose>
                        </div>
                    </DialogHeader>

                    <div className="space-y-5 px-4 py-4">
                        <p className="text-xs font-normal text-[var(--sub-text-color)]">
                            {config.description}
                        </p>

                        {/* Dynamic Fields */}
                        {(config?.fields || []).map(renderField)}

                        {/* OAuth Option for Email Configuration */}
                        {configType === "EMAIL" && !isOAuthEnabled && (
                            <>
                                {/* <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-gray-300" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-white px-2 text-gray-500">Or</span>
                                    </div>
                                </div> */}

                                <div className="space-y-2">
                                    <Button
                                        onClick={handleLinkGmail}
                                        disabled={isLinkingGmail}
                                        variant="secondary"
                                        className="w-full "
                                    >
                                        {isLinkingGmail ? (
                                            <>
                                                <span >Linking...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Link className="h-4 w-4" />
                                                Link Gmail Account
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}

                        {/* Show Gmail Linked Status */}
                        {configType === "EMAIL" && isOAuthEnabled && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between p-3 bg-sahi-green-light border border-sahi-green rounded-md">
                                    <div className="flex items-center gap-2">
                                        <Check className="h-4 w-4 text-sahi-green" />
                                        <span className="text-sm font-medium text-sahi-green">Gmail Linked</span>
                                    </div>
                                    <TooltipProvider>
                                        <Tooltip delayDuration={300}>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setDialogStates(prev => ({ ...prev, confirmUnlink: true }))}
                                                    className="h-7 w-7 p-0 hover:bg-sahi-red-light rounded-md"
                                                >
                                                    <X className="h-4 w-4 text-sahi-red" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="left" align="center" className="bg-slate-800 text-white">
                                                <p>Unlink Gmail Account</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        )}

                        <div className={`flex sm:flex-row sm:gap-3 items-center gap-2 justify-start`}>
                            {configType === "GOOGLE_REVIEW_LINK" && <Button
                                onClick={handleSaveConfiguration}
                                className="w-auto md:mt-0"
                            >
                                <Check className="h-4 w-4" />
                                Save Configuration
                            </Button>}
                            <Button
                                onClick={handleClose}
                                variant="secondary"
                                className="sm:flex-none sm:min-w-[100px]"
                            >
                                <X className="h-4 w-4  " />
                                Close
                            </Button>
                            {/* {configType === "GOOGLE_REVIEW_LINK" && (
                                <div className="w-full sm:w-auto flex justify-start md:justify-end">
                                    <Button
                                        onClick={handleTutorialClick}
                                        variant="secondary"
                                        className="w-auto px-4 sm:w-auto mt-2 md:mt-0"
                                    >
                                        <PlayCircle className="h-4 w-4" />
                                        Tutorial
                                    </Button>
                                </div>
                            )} */}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Unlink Confirmation AlertBox */}
            <AlertBox
                open={dialogStates.confirmUnlink}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmUnlink: open }))}
                title="Please Confirm"
                description="Are you sure you wish to unlink your Gmail account?"
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className="h-4 w-4" />
                    },
                    {
                        label: "Yes, Unlink",
                        variant: "action",
                        value: "confirm",
                        className: "w-full sm:w-auto",
                        icon: <Check className="h-4 w-4" />
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "confirm") {
                        handleUnlinkGmail();
                    }
                    setDialogStates(prev => ({ ...prev, confirmUnlink: false }));
                }}
            />
        </>
    );
}